import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../lib/api";

// Generic schema-driven form renderer for pratica modules.
// Supports two schema styles:
// 1. Local JS schemas (formSchemas.js): sections with fields[{path, showIf: fn}]
// 2. Remote declarative schemas (backend /pratiche/modules/{code}/schema):
//    sections with {type: "single"|"list", path, fields|columns[{key,...}]}
export const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm";

function getPath(obj, path) {
    if (!path) return obj;
    return path.split(".").reduce((cur, k) => (cur == null ? undefined : cur[k]), obj);
}

function evalShowIf(showIf, form) {
    if (!showIf) return true;
    if (typeof showIf === "function") return showIf(form);
    const v = getPath(form, showIf.field);
    if (showIf.equals !== undefined) return v === showIf.equals;
    if (showIf.in) return showIf.in.includes(v);
    return true;
}

export function ModuleForm({ schema, form, editing, updField }) {
    if (!schema) return null;
    return (
        <>
            {schema.sections
                .filter((s) => evalShowIf(s.showIf, form))
                .map((section) =>
                    section.type === "list" ? (
                        <ListSection key={section.code || section.title} section={section} form={form} editing={editing} updField={updField} />
                    ) : (
                        <SingleSection key={section.code || section.title} section={section} form={form} editing={editing} updField={updField} />
                    )
                )}
        </>
    );
}

function fieldPath(section, field) {
    if (field.path) return field.path;
    return section.path ? `${section.path}.${field.key}` : field.key;
}

function SingleSection({ section, form, editing, updField }) {
    const fields = section.fields || [];
    return (
        <Section title={section.title}>
            {fields
                .filter((f) => evalShowIf(f.showIf, form))
                .map((field) => {
                    const path = fieldPath(section, field);
                    return (
                        <Row key={path} label={field.label + (field.required ? " *" : "")}>
                            {editing ? (
                                <FieldInput field={field} value={getPath(form, path)} onChange={(v) => updField(path, v)} />
                            ) : (
                                <V value={displayValue(field, getPath(form, path))} />
                            )}
                        </Row>
                    );
                })}
        </Section>
    );
}

function ListSection({ section, form, editing, updField }) {
    const rows = getPath(form, section.path) || [];
    const cols = section.columns || section.fields || [];
    if (!editing && rows.length === 0) {
        return (
            <div className="border-b border-border last:border-b-0">
                <div className="px-5 py-2.5 bg-background-alt/50 text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">{section.title}</div>
                <div className="px-5 py-3 text-xs text-muted-foreground">Nessuna riga compilata</div>
            </div>
        );
    }
    const setRows = (next) => updField(section.path, next);
    const addRow = () => setRows([...rows, {}]);
    const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
    const updCell = (i, key, v) => {
        const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r));
        setRows(next);
    };

    return (
        <div className="border-b border-border last:border-b-0">
            <div className="px-5 py-2.5 bg-background-alt/50 text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground flex items-center justify-between">
                <span>{section.title}</span>
                {editing && (
                    <button
                        type="button"
                        data-testid={`add-row-${section.code || section.path}`}
                        onClick={addRow}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors normal-case tracking-normal"
                    >
                        <Plus size={11} /> {section.item_label || "Riga"}
                    </button>
                )}
            </div>
            <div className="px-5 py-4 overflow-x-auto">
                {rows.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Nessuna riga. {editing ? "Aggiungi la prima riga." : ""}</div>
                ) : (
                    <table className="w-full text-sm min-w-[600px]">
                        <thead>
                            <tr>
                                {cols.map((c) => (
                                    <th key={c.key} className="text-start pb-2 pe-3 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{c.label}</th>
                                ))}
                                {editing && <th className="w-8"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i} className="border-t border-border/60">
                                    {cols.map((c) => (
                                        <td key={c.key} className="py-1.5 pe-3 align-top">
                                            {editing ? (
                                                <FieldInput field={c} value={row?.[c.key]} onChange={(v) => updCell(i, c.key, v)} compact />
                                            ) : (
                                                <V value={displayValue(c, row?.[c.key])} />
                                            )}
                                        </td>
                                    ))}
                                    {editing && (
                                        <td className="py-1.5">
                                            <button type="button" onClick={() => removeRow(i)} className="text-muted-foreground hover:text-orange-600 transition-colors p-1">
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function displayValue(field, v) {
    if (field.type === "select" && field.options) {
        const opt = field.options.find((o) => o.value === v);
        if (opt) return opt.label;
    }
    return v;
}

function FieldInput({ field, value, onChange, compact }) {
    const cls = compact ? inputCls + " !py-1.5 !px-2 text-xs" : inputCls;
    const common = { "data-testid": field.testid, className: cls };
    if (field.reference_table) {
        return <ReferenceInput field={field} value={value} onChange={onChange} className={cls} />;
    }
    if (field.type === "select") {
        return (
            <select {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
                <option value="">—</option>
                {field.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        );
    }
    if (field.type === "date") {
        return <input {...common} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    }
    if (field.type === "number") {
        return <input {...common} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} />;
    }
    if (field.type === "boolean") {
        return (
            <select {...common} value={value === true ? "true" : value === false ? "false" : ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}>
                <option value="">—</option>
                <option value="true">Sì</option>
                <option value="false">No</option>
            </select>
        );
    }
    if (field.type === "textarea") {
        return <textarea {...common} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    }
    return (
        <input
            {...common}
            value={value || ""}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.upper ? e.target.value.toUpperCase() : e.target.value)}
        />
    );
}

function ReferenceInput({ field, value, onChange, className }) {
    const [query, setQuery] = useState(value || "");
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => { setQuery(value || ""); }, [value]);
    useEffect(() => {
        if (!open || query.trim().length < 1) return;
        const timer = setTimeout(async () => {
            try {
                const response = await api.get(`/comunica/reference-data/${field.reference_table}`, { params: { q: query, limit: 25 } });
                setRows(response.data.rows || []);
            } catch { setRows([]); }
        }, 180);
        return () => clearTimeout(timer);
    }, [field.reference_table, open, query]);

    const choose = (row) => {
        const selected = field.reference_table === "COM" ? row.description : row.code;
        setQuery(selected);
        onChange(selected);
        setOpen(false);
    };
    return (
        <div className="relative">
            <input
                className={className}
                value={query}
                placeholder={`Cerca in ${field.reference_table}...`}
                onFocus={() => setOpen(true)}
                onChange={(event) => { setQuery(event.target.value); onChange(event.target.value); setOpen(true); }}
                autoComplete="off"
            />
            {open && rows.length > 0 && (
                <div className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-orange-300 bg-white shadow-lg">
                    {rows.map((row, index) => (
                        <button key={`${row.code}-${row.province || ""}-${index}`} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(row)} className="block w-full border-b border-orange-100 px-3 py-2 text-left text-xs hover:bg-orange-50">
                            <span className="font-mono font-bold text-orange-800">{row.code}</span> — {row.description}
                            {row.province && <span className="text-black/60"> · {row.province} · {row.cap}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function Section({ title, children }) {
    return (
        <div className="border-b border-border last:border-b-0">
            <div className="px-5 py-2.5 bg-background-alt/50 text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">{title}</div>
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {children}
            </div>
        </div>
    );
}

export function Row({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</div>
            {children}
        </div>
    );
}

export function V({ value }) {
    return <div className="text-sm font-medium text-foreground min-h-[20px]">{value || value === 0 ? String(value) : <span className="text-muted-foreground">—</span>}</div>;
}
